import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';

import {
  AssociateReport,
  ServiceAppointment as Appointment,
  Address,
  Associate,
  Customer,
  Employee,
  Vehicle,
  Location,
} from '@quorum/models/xs-resource';
import {
  AssociateReportQueryParameters,
  AssociateQueryParameters,
  ServiceAppointmentQueryParameters as AppointmentQueryParameters,
  VehicleQueryParameters,
} from '@quorum/models/xs-query';

import { Actions, ofType } from '@ngrx/effects';
import { AppointmentsEntityActionTypes, UpdateAppointmentOnServerSuccess } from '@quorum/sha-entities/fixed-operations';
import { ApiService } from 'libs/api/src/lib/api.service';
import { AuthenticationStateService } from 'libs/authentication/src/lib/authentication-state.service';
import { ServiceLaneDropdownsFacade } from 'libs/service-lane-dropdowns/src/lib/+state/service-lane-dropdowns.facade';
import { ServiceLaneFacade } from 'libs/service-lane-root/src/lib/+state/service-lane.facade';
import { SystemControlStateService } from 'libs/xsr-system-control/src/lib/system-control-state.service';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'svc-freeform-appointment-matching',
  templateUrl: './freeform-appointment-matching.component.html',
  styleUrls: ['./freeform-appointment-matching.component.scss'],
})
export class FreeformAppointmentMatchingComponent implements OnInit, OnDestroy {
  customerMatchingForm = this.fb.group({
    createOrMatch: [1, Validators.required],
    search: [''],
    selectedAssociate: [null, Validators.required],
  });

  vehicleMatchingForm = this.fb.group({
    createOrMatch: [1, Validators.required],
    search: [''],
    selectedVehicle: [null, Validators.required],
  });

  matchingForm = this.fb.group({
    selectedAssociate: [null, Validators.required],
    selectedVehicle: [null, Validators.required],
  });
  associates$: Observable<AssociateReport[]> | BehaviorSubject<AssociateReport[]>;
  vehicles$: BehaviorSubject<Vehicle[]> = new BehaviorSubject<Vehicle[]>([]);
  existingAssociateMatches$: Observable<AssociateReport[]> | BehaviorSubject<AssociateReport[]>;
  selectedAssociate: AssociateReport = null;
  selectedVehicle: Vehicle = null;
  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  isLoadingExistingAssociates$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  isLoadingVehicle$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  isLoadingExistingAssociateVehicle$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  existingAssociateVehicleMatches$: BehaviorSubject<Vehicle[]> = new BehaviorSubject<Vehicle[]>([]);
  appointment$: Observable<Appointment>;
  queryParamSub$: any;
  queryParamSubExistingAssociates$: any;
  customerVehicles: Vehicle[] = [];
  newVehicle: Vehicle = new Vehicle();
  selectedStepIndex: number = 0;
  newAssociate: Associate = new Associate({ ...new Associate(), classId: 'INDIV' });
  newAddress: Address = new Address();
  matchSubscription: Subscription;
  customerMatchingSub: Subscription;
  employee$: Observable<Employee>;
  disableMatchButton: boolean = false;
  constructor(
    private fb: FormBuilder,
    private serviceLaneFacade: ServiceLaneFacade,
    private apiService: ApiService,
    private route: ActivatedRoute,
    public serviceLaneDropdownFacade: ServiceLaneDropdownsFacade,
    private systemControlStateService: SystemControlStateService,
    private authenticationStateService: AuthenticationStateService,
    private router: Router,
    private snackBar: MatSnackBar,
    private actions$: Actions,
    private serviceLaneDropdownsFacade: ServiceLaneDropdownsFacade
  ) {}
  @ViewChild('associateSearch', { static: false }) associateSearch: ElementRef;
  @ViewChild('stepper', { static: false }) private myStepper: MatStepper;
  ngOnInit() {
    this.isLoadingVehicle$.next(false);
    this.appointment$ = this.serviceLaneFacade.selectedAppointment$.pipe(filter((appointment) => appointment != null));
    this.employee$ = this.authenticationStateService.selectAuthenticatedEmployee();
    this.queryParamSub$ = new BehaviorSubject<any>(null).pipe(
      switchMap((queryParams: AssociateReportQueryParameters) => {
        if (queryParams) {
          return this.apiService.get<AssociateReport[]>('v/1/reporting/associates', { params: queryParams }).pipe(
            map((associates) => {
              return { queryParams: queryParams, associates: associates };
            })
          );
        } else {
          return of({
            queryParams: { search: '' },
            associates: [],
          });
        }
      }),
      shareReplay(1)
    );

    this.queryParamSubExistingAssociates$ = new BehaviorSubject<any>(null).pipe(
      switchMap((queryParams: AssociateReportQueryParameters) => {
        if (queryParams) {
          return this.apiService.get<AssociateReport[]>('v/1/reporting/associates', { params: queryParams }).pipe(
            map((associates) => {
              return { queryParams: queryParams, associates: associates };
            })
          );
        } else {
          return of({ queryParams: {}, associates: [] });
        }
      }),
      shareReplay(1)
    );

    this.existingAssociateMatches$ = this.queryParamSubExistingAssociates$.pipe(
      map((associatesObject: any) => {
        return associatesObject.associates;
      }),
      tap(() => this.isLoadingExistingAssociates$.next(false))
    );

    this.associates$ = this.route.queryParams.pipe(
      distinctUntilKeyChanged('search'),
      switchMap((params) => {
        return combineLatest(
          params.search
            ? this.apiService.get<AssociateReport[]>('v/1/reporting/associates', {
                params: new AssociateReportQueryParameters({
                  search: params.search,
                  embed: 'associate,addresses',
                }),
              })
            : of(null),
          params.phoneNumber
            ? this.apiService.get<AssociateReport[]>('v/1/reporting/associates', {
                params: new AssociateReportQueryParameters({
                  search: params.phoneNumber,
                  embed: 'associate,addresses',
                }),
              })
            : of(null),
          this.appointment$
        ).pipe(
          map(([nameMatchedAssociates, phoneMatchedAssociates, appointment]) => {
            let concatArray: Associate[] = [];
            if (phoneMatchedAssociates != null) {
              concatArray = concatArray.concat(phoneMatchedAssociates);
            }
            if (nameMatchedAssociates != null) {
              concatArray = concatArray.concat(nameMatchedAssociates);
            }

            let idArray: any[] = [];
            let finalArray: any[] = [];

            concatArray.forEach((el: Associate) => {
              if (idArray.indexOf(el.id) === -1) {
                idArray.push(el.id);
                finalArray.push(el);
              } else {
                finalArray.splice(finalArray.indexOf(finalArray.find((associate) => associate.id === el.id)), 1);
                finalArray.splice(0, 0, el);
              }
            });

            let ascMatch = finalArray.find((asc) => asc.id == appointment.customerId);
            if (ascMatch) {
              this.customerMatchingForm.get('createOrMatch').disable();
              this.matchAssociate(ascMatch, this.myStepper);
            }

            return finalArray;
          }),
          tap(() => this.isLoading$.next(false)),
          shareReplay(1)
        );
      })
    );

    this.customerMatchingSub = this.customerMatchingForm['controls'].search.valueChanges
      .pipe(
        filter((input: string) => input != null && input.length >= 3),
        debounceTime(800),
        distinctUntilChanged()
      )
      .subscribe((value: any) => {
        if (value && value.length > 0) {
          this.isLoadingExistingAssociates$.next(true);
          let queryParams: AssociateQueryParameters = new AssociateQueryParameters({
            search: value,
            embed: ['associate', 'addresses'],
          });
          <BehaviorSubject<AssociateQueryParameters>>this.queryParamSubExistingAssociates$.next(queryParams);
        }
      });
  }

  ngOnDestroy() {
    if (this.matchSubscription) {
      this.matchSubscription.unsubscribe();
    }
    this.queryParamSub$.unsubscribe();
    this.customerMatchingSub.unsubscribe();
  }

  matchAssociate(associate: AssociateReport, stepper: MatStepper) {
    this.isLoadingVehicle$.next(true);
    this.selectedAssociate = associate;
    this.newVehicle.associateId = associate.id;
    this.customerMatchingForm.get('selectedAssociate').setValue(associate);
    this.matchingForm.get('selectedAssociate').setValue(associate);
    stepper.selectedIndex = 1;
    let vehicleSearchParams: VehicleQueryParameters = new VehicleQueryParameters();
    console.log(associate);
    vehicleSearchParams.associateId = associate.id;
    vehicleSearchParams.embed = ['associate'];
    this.apiService
      .get('v/1/vehicles/vehicles', { params: vehicleSearchParams })
      .pipe(
        map((vehicles) => {
          console.log(vehicles);
          this.isLoadingVehicle$.next(false);
          this.vehicles$.next(vehicles);

          this.existingAssociateVehicleMatches$.next(vehicles);
        })
      )
      .subscribe();
  }

  matchVehicle(vehicle: Vehicle, stepper: MatStepper) {
    this.selectedVehicle = vehicle;
    this.vehicleMatchingForm.get('selectedVehicle').setValue(vehicle);
    this.matchingForm.get('selectedVehicle').setValue(vehicle);
    stepper.selectedIndex = 2;
  }

  newVehicleFormValidity(event: any) {
    //console.log(event);
  }

  vehicleEmitted(event: any) {
    this.selectedVehicle = event.vehicle;
    this.vehicleMatchingForm.get('selectedVehicle').setValue(event.vehicle);
    this.matchingForm.get('selectedVehicle').setValue(event.vehicle);
  }

  newAssociateResources(event: { associate: Associate; address: Address }) {
    // this.newAssociate = event.associate;
    // this.newAddress = event.address;
    let associate: Associate = new Associate();
    associate.emailAddress = event.associate.emailAddress;
    associate.firstName = event.associate.firstName;
    associate.lastName = event.associate.lastName;
    associate.messagePreferenceId = event.associate.messagePreferenceId;
    associate.classId = event.associate.classId;

    let newAddress: Address = new Address({
      cellPhoneNumber: event.address.cellPhoneNumber ? event.address.cellPhoneNumber.replace(/\D+/g, '') : null,
      homePhoneNumber: event.address.homePhoneNumber ? event.address.homePhoneNumber.replace(/\D+/g, '') : null,
      addressLine1: event.address.addressLine1,
      locationId: event.address.locationId,
      provStateId: event.address.provStateId,
      countryId: event.address.countryId,
      postalZipCode: event.address.postalZipCode,
    });

    let temp: AssociateReport = new AssociateReport();
    temp.firstName = event.associate.firstName;
    temp.lastName = event.associate.lastName;
    temp.cellPhoneNumber = event.address.cellPhoneNumber;
    temp.homePhoneNumber = event.address.homePhoneNumber;
    temp.emailAddress = event.associate.emailAddress;
    temp.messagePreferenceId = event.associate.messagePreferenceId;
    temp.embedded = {};
    temp.embedded.associate = associate;
    temp.embedded.addresses = newAddress;
    this.selectedAssociate = temp;
    this.customerMatchingForm.get('selectedAssociate').setValue(this.selectedAssociate);
  }
  autoFocusSearch(value: number) {
    if (value === 2) {
      setTimeout(() => this.associateSearch.nativeElement.focus(), 250);
    }
  }

  confirmMatch(appointment: Appointment, employee: Employee) {
    let associateObject: AssociateReport = this.customerMatchingForm.get('selectedAssociate').value;
    let vehicleObject: Vehicle = this.vehicleMatchingForm.get('selectedVehicle').value;
    let associateAddressObject: Address = associateObject.embedded.addresses;

    let associate$: Observable<Associate>;

    let snackBarRef = this.snackBar.open(`Matching appointment...`);
    if (associateObject.embedded.associate.id == null) {
      //need to create the associate and address
      associate$ = this.apiService
        .post<Associate>(`v/1/associates/associates`, associateObject.embedded.associate)
        .pipe(
          switchMap((associate) => {
            if (isNaN(Number(associateAddressObject.locationId))) {
              let newLocation: Location = {
                ...new Location(),
                countryId: associateAddressObject.countryId,
                description: associateAddressObject.locationId,
                postalCode: null,
                provStateId: associateAddressObject.provStateId,
              };

              return this.apiService.post<Location>('v/1/associates/locations', newLocation).pipe(
                switchMap((newlyCreatedLocation) => {
                  let addressWithNewlyCreatedLocation: Address = new Address({
                    ...associateAddressObject,
                    associateId: associate.id,
                    locationId: newlyCreatedLocation.id,
                    cellPhoneNumber: associateAddressObject.cellPhoneNumber
                      ? associateAddressObject.cellPhoneNumber.replace(/\D+/g, '')
                      : null,
                    homePhoneNumber: associateAddressObject.homePhoneNumber
                      ? associateAddressObject.homePhoneNumber.replace(/\D+/g, '')
                      : null,
                  });

                  return this.apiService
                    .post<Address>(
                      `v/1/associates/associates/${associate.id}/addresses`,
                      addressWithNewlyCreatedLocation
                    )
                    .pipe(
                      map((addressWithNewlyCreatedLocation) => {
                        /* fetch all the locations to have the newly created location available in the Vehicle Location dropdown list */
                        this.serviceLaneDropdownsFacade.getLocationsFromServer();
                        return associate;
                      })
                    );
                })
              );
            } else {
              let address: Address = new Address({ ...associateObject.embedded.addresses, associateId: associate.id });
              return this.apiService.post<Address>(`v/1/associates/associates/${associate.id}/addresses`, address).pipe(
                map((address) => {
                  return associate;
                })
              );
            }
          }),
          switchMap((associate) => {
            return this.authenticationStateService.selectAuthenticatedEmployee().pipe(
              switchMap((employee) => {
                return this.systemControlStateService
                  .getSystemControlValue('CL_ACCEPT_CHECK_FLAG', employee.rooftopId)
                  .pipe(
                    take(1),
                    switchMap((sct) => {
                      let customer: Customer = {
                        ...new Customer(),
                        associateId: associate.id,
                        shopFlag: '1',
                        acceptCheque: sct.value,
                      };
                      return this.apiService.post<Customer>(`v/1/associates/customers`, customer).pipe(
                        map((customer) => {
                          return associate;
                        })
                      );
                    })
                  );
              })
            );
          })
        );
    } else {
      //associate exists;
      associate$ = of(associateObject.embedded.associate);
    }

    this.matchSubscription = associate$
      .pipe(
        switchMap((associate) => {
          if (vehicleObject.id == null) {
            vehicleObject.associateId = associate.id;
            vehicleObject.rooftopId = employee.rooftopId;
            return this.apiService
              .post<Vehicle>(`v/1/vehicles/vehicles`, vehicleObject, { params: { disableTranslation: true } })
              .pipe(
                map((vehicle) => {
                  return { vehicle, associate };
                })
              );
          } else {
            return of({ vehicle: vehicleObject, associate: associate });
          }
        })
      )
      .subscribe((data) => {
        //update appointment
        let updatedAppointment: Appointment = new Appointment({
          ...appointment,
          customerId: data.associate.id,
          vehicleId: data.vehicle.id,
          vehicleVin: data.vehicle.vin,
        });

        this.serviceLaneFacade;

        this.serviceLaneFacade.updateAppointmentOnServer(
          updatedAppointment,
          new AppointmentQueryParameters({
            embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
          })
        );

        this.actions$
          .pipe(
            ofType(AppointmentsEntityActionTypes.UpdateAppointmentOnServerSuccess),
            map((action: UpdateAppointmentOnServerSuccess) => {
              return action.payload;
            }),
            take(1)
          )
          .subscribe((apt) => {
            console.log(`THIS THING IS STILL ACTIVE`);
            this.serviceLaneFacade.updateAppointment(apt);
            snackBarRef.dismiss();
            this.router.navigateByUrl(`/vehicle/${apt.vehicleId}/appointments/${apt.id}`);
          });
      });
  }
}
